import { CREATOR_SHOP_SORT, PRODUCT_LIST_SORT } from '../constant';
import { ProductThumbnail, ProductThumbnailOption, ProductThumbnails } from './thumbnail';
import { ProductTagBaseProps } from '@services/MarppleShop/renderApp/components/cells/ProductTag/ProductTagBase.ts';
import { ProductMeta } from '@services/MarppleShop/features/ProductMeta';
import { GoodsTypeId } from '@services/MarppleShop/features/Product/type';

export interface ProductCardViewData {
  id: number;

  category: {
    root_id: number;
    list_id: number;
    item_id: number;
  };

  sell_start_at: Date | null;
  sell_end_at: Date | null;
  stores_product_id: number;

  /**
   * 성인인증이 필요한 상품인지
   */
  adult_check_needed: boolean;

  is_quantity_public: boolean;
  store_id: number;
  store_name: string;
  review_score: number;
  review_count: number;
  order_count: number;
  place_idx?: number;
  domain_name: string;

  thumbnail_info: ProductThumbnail;

  /**
   * @deprecated Use `thumbnail_info.imageUrl`
   * @see {createLegacyThumbnail}
   */
  thumbnails: ProductThumbnails;

  /**
   * @deprecated Use `thumbnail_info.scale`
   * @see {createLegacyThumbnail}
   */
  thumbnail_ratio: number;

  profile_image_url: string | null;
  quantity: number;

  name: string;

  price: number;
  price_en: number | null;
  price_jp: number | null;
  out_of_stock: boolean;
  is_liked: boolean;
  public_at: Date;
  ranking?: number;
  sold_quantity?: number;
  max_purchase_per_user?: number;
  goods_type_id: GoodsTypeId;

  /**
   * `실시간` 뱃지 그릴 때 사용
   */
  ordered_at?: Date;

  /**
   * @description `Membership` 뱃지 그릴 때 사용
   */
  is_membership: boolean;

  /**
   * "n명이 보는 중" 뱃지 그릴 때 주입
   */
  watcher_count?: number;

  is_token_gate?: boolean;

  is_public: boolean;

  /**
   * 급상승
   */
  is_trending?: boolean;

  /**
   * 인기
   */
  is_popular?: boolean;

  is_funding?: boolean;

  is_brand_deal?: boolean;

  /**
   * @deprecated
   * 상품 메타데이터
   */
  meta?: ProductMeta;

  user_id: number | null;
}

export enum ProductCardTheme {
  light = 'light',
  dark = 'dark',
}

/**
 * 수평형 카드의 경우 xSmall 사이즈만 지원합니다.
 */
export enum ProductCardSize {
  xSmall = 'xSmall',
  small = 'small',
  medium = 'medium',
  large = 'large',
}

/**
 * `ProductCardView` 컴포넌트를 그리는데 필요한 옵션 타입입니다.
 */
export type ProductCardOption = ProductThumbnailOption & {
  size: ProductCardSize;

  /**
   * @default light
   */
  theme?: ProductCardTheme;

  /**
   * @description 뱃지 목록 숨기기
   * @default false
   */
  hide_badges?: boolean;

  /**
   * @description 하단 태그 숨기기
   * @default false
   */
  hide_tag?: boolean;

  /**
   * @description 리뷰 숨기기
   * @default false
   *
   * - 리뷰가 존재하지 않는 사이즈에서는 활성화해도 리뷰 영역이 보이지 않습니다.
   * - 리뷰가 존재하는 사이즈에서 활성화하면 리뷰 영역을 가립니다.
   */
  hide_review?: boolean;

  /**
   * 스토어명, 상품명, 가격 등 하단 정보 숨기는 옵션
   *
   * 이 옵션을 켜면 좋아요 버튼이 우측 상단에 보여집니다.
   * @default false
   */
  hide_info?: boolean;

  is_horizontal?: boolean;

  /**
   * hidden 상태로 컴포넌트 생성할 때 활성화
   */
  is_hidden?: boolean;

  /**
   * 카드 루트에 주입할 클래스
   */
  card_klass?: string;

  realtime_tag?: ProductTagBaseProps;

  injectedStyles?: string[];
};

/**
 * `ProductCardListView` 컴포넌트를 그리는데 필요한 데이터 타입입니다.
 */
export type ProductCardListData = {
  products: ProductCardViewData[];
};

/**
 * paginated 상품 목록을 조회했을 때 응답 데이터입니다.
 */
export type PaginatedProductListResponse = {
  products: ProductCardViewData[];
  total_count: number;
};

export type ProductResponse = {
  product: ProductCardViewData | null;
};

export type DefaultProductListSort = keyof typeof PRODUCT_LIST_SORT;
export type CreatorShopProductListSort = keyof typeof CREATOR_SHOP_SORT;

export interface SortOption {
  key: keyof typeof PRODUCT_LIST_SORT;
  value: string;
  name: string;
}

export interface CreatorProductSortOption {
  key: keyof typeof CREATOR_SHOP_SORT;
  value: string;
  name: string;
}

export type UserLikedProductListFetchOption = {
  limit: number;
  offset?: number;
};

export type UserLikedProductListReqData = {
  limit?: string;
  offset?: string;
};

export const bestProductTypeMap = {
  realtime: 'realtime',
  weekly: 'weekly',
  monthly: 'monthly',
} as const;

export const PRODUCT_SALE_STATUS = {
  /**
   * 판매 시작 전
   */
  BEFORE_SALE: 'BEFORE_SALE',

  /**
   * 판매 중 (상시 판매)
   */
  ON_SALE: 'ON_SALE',

  /**
   * 판매 중 (한정 판매)
   */
  LIMITED_SALE: 'LIMITED_SALE',

  /**
   * 판매 종료
   */
  ENDED_SALE: 'ENDED_SALE',
} as const;

export type ProductSaleStatusType = (typeof PRODUCT_SALE_STATUS)[keyof typeof PRODUCT_SALE_STATUS];
